import {
  InlineAlertType,
  Typenames,
  UserAvatarAssetResultTypenames,
} from '@varo/graphql-schema-types';
import OfferListItem from 'features/rewards/components/OfferListItem/OfferListItem';
import { useMyOffersContext } from 'features/rewards/screens/MyOffers/providers/MyOffersProvider';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleSmText from 'shared/components/vxl/typography/primary/title/TitleSmText';
import theme from 'shared/styles/theme';
import { tagColorMapper } from 'shared/utils/tagColorMapper/tagColorMapper';

interface UpsideOffersListProps {
  showSectionHeader: boolean;
  showFooterSubtitle: boolean;
}

const UpsideOffersList = ({
  showSectionHeader,
  showFooterSubtitle,
}: UpsideOffersListProps) => {
  const insets = useSafeAreaInsets();
  const {
    upsideOffers: offers,
    upsideSectionTitle: sectionTitle,
    upsideSectionSubtitle: sectionSubtitle,
    inlineAlertBanner,
    onOfferClick,
  } = useMyOffersContext();

  return (
    <View style={{ paddingBottom: insets.bottom }}>
      {showSectionHeader ? (
        <View style={styles.textContainer}>
          <TitleSmText style={styles.sectionTitle}>{sectionTitle}</TitleSmText>
          <BodySmText style={styles.subtitle}>{sectionSubtitle}</BodySmText>
        </View>
      ) : null}

      {inlineAlertBanner && offers.length > 0 ? (
        <View style={styles.inlineContainer}>
          <InlineAlert
            alertType={InlineAlertType.Info}
            bodyText={inlineAlertBanner.title ?? ''}
            iconKeyType="INFO"
          />
        </View>
      ) : null}

      {offers.map((offer, index) => {
        const isLastItem = index === offers.length - 1;
        const offerId =
          'id' in offer && typeof offer.id === 'string'
            ? offer.id
            : `activated-offer-${offer.primaryText}-${index}`;

        return (
          <View key={offerId}>
            <OfferListItem
              id={offerId}
              titleText={offer.primaryText}
              subtitleText={offer.secondaryText ?? ''}
              subtitleTextSecondary={offer.companionExtra ?? ''}
              distance={offer.tertiaryText?.distance ?? undefined}
              originalPrice={offer.tertiaryText?.originalPrice?.formatted}
              discountedPrice={offer.tertiaryText?.disountedPrice?.formatted}
              address={offer.tertiaryText?.addressText ?? undefined}
              iconUrl={
                offer.leadingAccessory?.__typename === Typenames.UserAvatar &&
                offer.leadingAccessory.userAvatarAsset?.__typename ===
                  UserAvatarAssetResultTypenames.URLType
                  ? offer.leadingAccessory.userAvatarAsset?.url
                  : undefined
              }
              tagText={
                offer.trailingAccessory?.__typename === Typenames.Tag
                  ? offer.trailingAccessory.text
                  : undefined
              }
              tagStyle={
                offer.trailingAccessory?.__typename === Typenames.Tag
                  ? tagColorMapper(offer.trailingAccessory.tagColor)
                  : undefined
              }
              tagIconKey={
                offer.trailingAccessory?.__typename === Typenames.Tag
                  ? offer.trailingAccessory.icon
                  : undefined
              }
              accessibilityLabel={`offer-item-${offer.primaryText}`}
              onPress={() => onOfferClick(offer)}
              isDisabled={false}
              analyticsId="activatedOfferClicked"
              customAttributes={
                offer.onClick?.analyticsEvent
                  ? parseGQLAnalyticsEvent(offer.onClick.analyticsEvent)
                      .properties
                  : null
              }
            />
            {!isLastItem ? (
              <View style={styles.divider}>
                <HairLineDivider />
              </View>
            ) : null}
          </View>
        );
      })}

      {showFooterSubtitle ? (
        <BodySmText style={styles.footerSubtitle}>{sectionSubtitle}</BodySmText>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  textContainer: {
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
  sectionTitle: {
    marginBottom: theme.spacing.xs,
  },
  subtitle: {
    color: theme.color.text.secondary,
  },
  divider: {
    marginHorizontal: theme.spacing.m,
  },
  inlineContainer: {
    marginBottom: theme.spacing.s,
    marginHorizontal: theme.spacing.m,
  },
  footerSubtitle: {
    color: theme.color.text.secondary,
    textAlign: 'center',
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.m,
    marginBottom: theme.spacing.s,
  },
});

export default UpsideOffersList;
